import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { apiFetch } from "../../../api/apiFetch";
import { requestNetworkEncouragement, sendAiFeedback } from "../../../api/aiIntelligence";
import {
  fetchNetworkChat,
  fetchNetworkChats,
  getNetworkPath,
  getSprintWorkspacePath,
  markNetworkChatRead,
  sendNetworkChatMessage,
  startSprint as startSprintFlow,
} from "../../../api/trustbuds";
import { BEHAVIOR_CHANGED_EVENT } from "../../behavior/utils/invalidation";
import {
  mapConnectionsToPeople,
} from "../utils/networkMappers";

function isMomentToday(moment) {
  if (!moment?.created_at) return false;
  const parsed = new Date(moment.created_at);
  if (Number.isNaN(parsed.getTime())) return false;
  return parsed.toDateString() === new Date().toDateString();
}

function percentageChange(today, yesterday) {
  if (!yesterday) return today > 0 ? "+100%" : "0%";
  const change = Math.round(((today - yesterday) / yesterday) * 100);
  return `${change >= 0 ? "+" : ""}${change}%`;
}

function buildMomentGlanceRows({ moments = [], summary = null } = {}) {
  const todayMoments = moments.filter(isMomentToday);
  const activeActors = new Set(todayMoments.map((moment) => moment?.actor?.id || moment?.actor?.username).filter(Boolean));
  const counts = summary?.counts || {};
  const hubCounts = summary?.accountability_hub?.counts || {};
  const activeToday = Number(counts.people_who_showed_up_today || counts.showing_up_today || 0) || activeActors.size;
  const reportsIn = Number(counts.accountability_reports_in || hubCounts.reports_in || 0);
  const socialMoments = todayMoments.filter((moment) => Number(moment?.comment_count || 0) > 0 || (moment?.reaction_summary || []).some((reaction) => Number(reaction?.count || 0) > 0)).length;
  return [
    {
      id: "moved",
      label: "Active today",
      value: String(activeToday),
      change: `${percentageChange(activeToday, 0)} today`,
      accent: "#1fbf8f",
      soft: "rgba(31, 191, 143, 0.12)",
    },
    {
      id: "actions",
      label: "Reports done",
      value: String(reportsIn || todayMoments.length),
      change: "From Daily Reports and Moments",
      accent: "#17345d",
      soft: "rgba(23, 52, 93, 0.12)",
    },
    {
      id: "updates",
      label: "Responses",
      value: String(socialMoments),
      change: "Reactions and replies today",
      accent: "#ff9a2f",
      soft: "rgba(255, 154, 47, 0.14)",
    },
  ];
}

export function useNetworkPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [connections, setConnections] = useState([]);
  const [threads, setThreads] = useState([]);
  const [moments, setMoments] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);
  const [loading, setLoading] = useState(true);
  const [threadsLoading, setThreadsLoading] = useState(true);
  const [momentsLoading, setMomentsLoading] = useState(true);
  const [threadLoading, setThreadLoading] = useState(false);
  const [error, setError] = useState("");
  const [moduleErrors, setModuleErrors] = useState({
    chats: "",
    connections: "",
    moments: "",
    health: "",
    summary: "",
  });
  const [composerValue, setComposerValue] = useState("");
  const [composerError, setComposerError] = useState("");
  const [sending, setSending] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [notice, setNotice] = useState({ message: "", severity: "info" });
  const [startingSprint, setStartingSprint] = useState(false);
  const [networkHealth, setNetworkHealth] = useState(null);
  const [networkSummary, setNetworkSummary] = useState(null);
  const [networkHealthDismissed, setNetworkHealthDismissed] = useState(false);
  const [encouragementOptions, setEncouragementOptions] = useState([]);
  const [encouragementLoading, setEncouragementLoading] = useState(false);
  const [aiComposedMessage, setAiComposedMessage] = useState(false);

  const activeTab = searchParams.get("tab") === "messages" ? "messages" : "moments";
  const selectedConnectionId = searchParams.get("connectionId") || "";
  const messageParam = searchParams.get("message") || "";
  const quickActionParam = searchParams.get("quickAction") || "";
  const composeParam = searchParams.get("compose") || "";

  const loadThreads = useCallback(async () => {
    setThreadsLoading(true);
    try {
      const data = await fetchNetworkChats();
      setThreads(Array.isArray(data) ? data : []);
      return Array.isArray(data) ? data : [];
    } finally {
      setThreadsLoading(false);
    }
  }, []);

  const loadConnections = useCallback(async () => {
    const data = await apiFetch("/connections/");
    setConnections(Array.isArray(data) ? data : []);
    return Array.isArray(data) ? data : [];
  }, []);

  const loadMoments = useCallback(async () => {
    setMomentsLoading(true);
    try {
      const data = await apiFetch("/moments/feed/");
      const items = Array.isArray(data?.items) ? data.items : [];
      setMoments(items);
      return items;
    } finally {
      setMomentsLoading(false);
    }
  }, []);

  const loadNetworkHealth = useCallback(async () => {
    const data = await apiFetch("/activity/health/");
    setNetworkHealth(data?.nudge ? data : null);
    return data || null;
  }, []);

  const loadNetworkSummary = useCallback(async () => {
    const data = await apiFetch("/activity/summary/");
    setNetworkSummary(data || null);
    return data || null;
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError("");
    setModuleErrors({ chats: "", connections: "", moments: "", health: "", summary: "" });
    try {
      const [threadsResult, connectionsResult, momentsResult, healthResult, summaryResult] = await Promise.allSettled([
        loadThreads(),
        loadConnections(),
        loadMoments(),
        loadNetworkHealth(),
        loadNetworkSummary(),
      ]);
      const nextModuleErrors = {
        chats:
          threadsResult.status === "rejected"
            ? threadsResult.reason?.message || "Unable to load chats."
            : "",
        connections:
          connectionsResult.status === "rejected"
            ? connectionsResult.reason?.message || "Unable to load connections."
            : "",
        moments:
          momentsResult.status === "rejected"
            ? momentsResult.reason?.message || "Unable to load Moments."
            : "",
        health:
          healthResult.status === "rejected"
            ? healthResult.reason?.message || "Unable to load network health."
            : "",
        summary:
          summaryResult.status === "rejected"
            ? summaryResult.reason?.message || "Unable to load network summary."
            : "",
      };
      setModuleErrors(nextModuleErrors);
      const failedModules = Object.values(nextModuleErrors).filter(Boolean).length;
      if (failedModules === Object.keys(nextModuleErrors).length) {
        setError("Unable to load network.");
      } else if (failedModules > 0) {
        setError("Some network sections did not load.");
      }
    } catch (err) {
      setError(err?.message || "Unable to load network.");
    } finally {
      setLoading(false);
    }
  }, [loadConnections, loadMoments, loadNetworkHealth, loadNetworkSummary, loadThreads]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  useEffect(() => {
    const handler = () => loadAll();
    window.addEventListener("trustbud:connections-changed", handler);
    window.addEventListener(BEHAVIOR_CHANGED_EVENT, handler);
    return () => {
      window.removeEventListener("trustbud:connections-changed", handler);
      window.removeEventListener(BEHAVIOR_CHANGED_EVENT, handler);
    };
  }, [loadAll]);

  useEffect(() => {
    let active = true;

    async function loadSelectedThread() {
      if (activeTab !== "messages" || !selectedConnectionId) {
        setSelectedThread(null);
        setComposerValue("");
        setComposerError("");
        return;
      }

      setThreadLoading(true);
      try {
        const data = await fetchNetworkChat(selectedConnectionId, 80);
        if (!active) return;
        setSelectedThread(data || null);
        setComposerError("");
        setEncouragementOptions([]);
        setAiComposedMessage(false);
        if (messageParam) {
          setComposerValue((current) => current || messageParam.slice(0, 280));
        } else if (composeParam === "1" && quickActionParam === "encourage") {
          setComposerValue((current) => current || "You've got this.");
        }
        await markNetworkChatRead(selectedConnectionId);
        if (!active) return;
        await loadThreads();
        window.dispatchEvent(new Event("trustbud:connections-changed"));
      } catch (err) {
        if (!active) return;
        setSelectedThread(null);
        setComposerError("");
        setError(err?.message || "Unable to load conversation.");
      } finally {
        if (active) {
          setThreadLoading(false);
        }
      }
    }

    loadSelectedThread();
    return () => {
      active = false;
    };
  }, [activeTab, composeParam, loadThreads, messageParam, quickActionParam, selectedConnectionId]);

  useEffect(() => {
    setNetworkHealthDismissed(false);
  }, [networkHealth?.nudge?.id]);

  const people = useMemo(() => mapConnectionsToPeople(connections), [connections]);
  const glanceRows = useMemo(
    () => buildMomentGlanceRows({ moments, summary: networkSummary }),
    [moments, networkSummary]
  );
  const activeSprintThread = useMemo(
    () => threads.find((thread) => thread?.activeSprint?.id) || null,
    [threads]
  );
  const recentThreads = useMemo(
    () =>
      threads.filter(
        (thread) => String(thread?.connectionId || "") !== String(activeSprintThread?.connectionId || "")
      ),
    [activeSprintThread, threads]
  );
  const selectedConnection = useMemo(
    () =>
      connections.find(
        (connection) => String(connection?.id || "") === String(selectedConnectionId || "")
      ) || null,
    [connections, selectedConnectionId]
  );

  const setTab = useCallback(
    (tab) => {
      navigate(getNetworkPath({ tab, connectionId: tab === "messages" ? selectedConnectionId : null }), {
        replace: true,
      });
    },
    [navigate, selectedConnectionId]
  );

  const openThread = useCallback(
    (connectionId) => {
      navigate(getNetworkPath({ tab: "messages", connectionId }));
    },
    [navigate]
  );

  const openInviteDialog = useCallback(() => setInviteOpen(true), []);
  const closeInviteDialog = useCallback(() => setInviteOpen(false), []);
  const openConnections = useCallback(() => navigate("/connections"), [navigate]);
  const openFindPeople = useCallback(() => navigate("/connections?tab=find"), [navigate]);

  const handlePersonClick = useCallback(
    (person) => {
      if (!person?.connectionId) return;
      navigate(`/network/person/${person.connectionId}`);
    },
    [navigate]
  );

  const openPersonProfile = useCallback(
    (source) => {
      const connectionId = source?.connectionId || source?.connection_id || null;
      if (!connectionId) return;
      navigate(`/network/person/${connectionId}`);
    },
    [navigate]
  );

  const handleMomentOpen = useCallback(
    (moment) => {
      if (moment?.id) {
        navigate(`/moments/${moment.id}`);
        return;
      }
      navigate("/activity?tab=moments");
    },
    [navigate]
  );

  const handleMomentReaction = useCallback(
    async (moment, reactionType) => {
      try {
        await apiFetch(`/moments/${moment.id}/reactions/`, {
          method: "POST",
          body: JSON.stringify({ reaction_type: reactionType }),
        });
        await loadMoments();
      } catch (err) {
        setNotice({ message: err?.message || "Unable to save that reaction.", severity: "error" });
      }
    },
    [loadMoments]
  );

  const openSprint = useCallback(() => {
    if (!selectedThread?.activeSprint?.id) return;
    navigate(
      getSprintWorkspacePath({
        sprintId: selectedThread.activeSprint.id,
        connectionId: selectedThread.connectionId,
        userId: selectedThread?.partner?.id,
      })
    );
  }, [navigate, selectedThread]);

  const startSprint = useCallback(async () => {
    if (!selectedThread?.connectionId) return;
    setStartingSprint(true);
    try {
      const result = await startSprintFlow(selectedThread.connectionId, {
        userId: selectedThread?.partner?.id,
        focus: "chat",
        source: "network",
      });
      window.dispatchEvent(new Event("trustbud:connections-changed"));
      if (result?.code === "already_active") {
        setNotice({ message: "You're already in Accountability with this person.", severity: "info" });
      }
      if (result?.code === "request_sent" || result?.code === "request_pending") {
        setNotice({ message: "Invite sent. They can accept it to start Accountability.", severity: "success" });
        return;
      }
      if (result?.route) navigate(result.route);
    } catch (err) {
      const activeSprintId = err?.errors?.active_sprint_id || err?.errors?.sprint_id || null;
      if (activeSprintId) {
        navigate(
          getSprintWorkspacePath({
            sprintId: activeSprintId,
            connectionId: selectedThread.connectionId,
            userId: selectedThread?.partner?.id,
            focus: "chat",
          })
        );
        return;
      }
      setNotice({
        message: err?.message || "Unable to start Accountability.",
        severity: "error",
      });
    } finally {
      setStartingSprint(false);
    }
  }, [navigate, selectedThread]);

  const messageSomeone = useCallback(() => {
    const fallbackThread =
      activeSprintThread?.connectionId || recentThreads[0]?.connectionId || connections[0]?.id || null;
    if (fallbackThread) {
      openThread(fallbackThread);
      return;
    }
    navigate("/activity?tab=messages");
  }, [activeSprintThread?.connectionId, connections, navigate, openThread, recentThreads]);

  const sendMessage = useCallback(async () => {
    if (!selectedThread?.connectionId) return;
    const body = composerValue.trim();
    if (!body) {
      setComposerError("Write a short message first.");
      return;
    }
    if (body.length > 280) {
      setComposerError("Message must be 280 characters or fewer.");
      return;
    }

    setSending(true);
    setComposerError("");
    const tempId = `temp-${Date.now()}`;
    const optimisticMessage = {
      id: tempId,
      body,
      created_at: new Date().toISOString(),
      sender: { id: "viewer", username: "You" },
    };
    setSelectedThread((current) =>
      current
        ? {
            ...current,
            messages: [...(current.messages || []), optimisticMessage],
          }
        : current
    );

    try {
      const response = await sendNetworkChatMessage(
        selectedThread.connectionId,
        body,
        selectedThread?.activeSprint?.id || null
      );
      const savedMessage = response?.message || response;
      setSelectedThread((current) =>
        current
          ? {
              ...current,
              messages: [...(current.messages || []).filter((item) => String(item.id) !== tempId), savedMessage],
            }
          : current
      );
      setComposerValue("");
      if (aiComposedMessage) {
        sendAiFeedback({
          action: "message_sent",
          taskType: "network_encouragement",
        }).catch(() => {});
        setAiComposedMessage(false);
      }
      window.dispatchEvent(new Event("trustbud:connections-changed"));
      await loadThreads();
    } catch (err) {
      setSelectedThread((current) =>
        current
          ? {
              ...current,
              messages: (current.messages || []).filter((item) => String(item.id) !== tempId),
            }
          : current
      );
      setComposerError(err?.message || "Unable to send message.");
    } finally {
      setSending(false);
    }
  }, [aiComposedMessage, composerValue, loadThreads, selectedThread]);

  const loadEncouragementOptions = useCallback(async () => {
    const targetUserId = selectedThread?.partner?.id;
    if (!targetUserId) return;
    setEncouragementLoading(true);
    setComposerError("");
    try {
      const payload = await requestNetworkEncouragement(targetUserId, {
        state: selectedThread?.context?.state || selectedThread?.activeSprint?.recovery?.state || "",
        active_sprint_id: selectedThread?.activeSprint?.id || null,
      });
      if (payload?.locked) {
        setNotice({ message: payload.detail || "Unlock Insights for suggested encouragement.", severity: "info" });
        setEncouragementOptions([]);
      } else {
        setEncouragementOptions(Array.isArray(payload?.options) ? payload.options : []);
      }
    } catch (err) {
      setComposerError(err?.message || "Unable to load encouragement options.");
    } finally {
      setEncouragementLoading(false);
    }
  }, [selectedThread, setNotice]);

  const useEncouragementOption = useCallback((message) => {
    setComposerValue(String(message || "").slice(0, 280));
    setAiComposedMessage(true);
  }, []);

  return {
    activeTab,
    loading,
    threadsLoading,
    momentsLoading,
    threadLoading,
    error,
    moduleErrors,
    people,
    moments,
    glanceRows,
    threads,
    activeSprintThread,
    recentThreads,
    selectedThread,
    selectedConnectionId,
    inviteOpen,
    notice,
    composerValue,
    composerError,
    encouragementOptions,
    encouragementLoading,
    sending,
    startingSprint,
    setComposerValue,
    setNotice,
    setTab,
    openThread,
    openInviteDialog,
    closeInviteDialog,
    openConnections,
    openFindPeople,
    handlePersonClick,
    openPersonProfile,
    handleMomentOpen,
    handleMomentReaction,
    networkHealth: networkHealthDismissed ? null : networkHealth?.nudge || null,
    networkSummary,
    dismissNetworkHealth: () => setNetworkHealthDismissed(true),
    messageSomeone,
    openSprint,
    startSprint,
    sendMessage,
    loadEncouragementOptions,
    useEncouragementOption,
    selectedConnection,
  };
}
